import type { UserInputTypes } from '@server/user-inputs';
import type {
  DocumentationTagAndMetadataElementsDto,
  DocumentationUserAndMetadataElementsDto,
  DocumentationUserInputMetadataElement,
} from '../../../types/documentationUserInputs';
import { userInputMetadataElementId } from './utils';
import {
  ConfigurationTypeToDocumentationTabType,
  DocumentationTabTypes,
} from '../../../types/enums/DocumentationTabTypes';
import type { ConfigurationItemMap } from '../configuration-item-tables/shared/helpers';
import { userInputMetadataElementToSalesforceItem } from '../helpers';
import groupBy from 'lodash/groupBy';
import type { SalesforceItem } from '../../parser/ParserTypes';

export function addSalesforceItemToConfigurationItemMap(
  map: ConfigurationItemMap,
  item: SalesforceItem,
): void {
  const id = item.metadataId;
  if (!id) return;
  const key = item.parentType;
  const list = map[key] ?? (map[key] = []);
  if (!list.some((i) => i.metadataId === id)) {
    list.push(item);
  }
}

export function removeMetadataElementIdFromConfigurationItemMap(
  map: ConfigurationItemMap,
  metadataElementId: string,
): void {
  for (const k of Object.keys(map)) {
    const list = map[k];
    if (!list?.length) continue;
    map[k] = list.filter((i) => i.metadataId !== metadataElementId);
  }
}

export type TagConfigurationsState = Record<
  string,
  { [tagKey in UserInputTypes]: ConfigurationItemMap }
>;

/** Builds grid-selector summary from per-tag/user configuration maps (Redux `tagsConfigurations`). */
export function buildTagsAndUsersCountSummaryFromTagConfigurations(
  tagsConfigurations: TagConfigurationsState,
): Record<string, Record<string, string[]>> {
  const summary: Record<string, Record<string, string[]>> = {};

  const push = (rowId: string, metadataId: string, tab: DocumentationTabTypes) => {
    if (tab === DocumentationTabTypes.OTHER) return;
    if (!summary[rowId]) summary[rowId] = {};
    const bucket = summary[rowId][tab] ?? (summary[rowId][tab] = []);
    if (!bucket.includes(metadataId)) bucket.push(metadataId);
  };

  for (const [rowId, configs] of Object.entries(tagsConfigurations)) {
    for (const map of Object.values(configs)) {
      for (const items of Object.values(map)) {
        for (const item of items) {
          const metadataId = item.metadataId;
          if (!metadataId) continue;
          push(rowId, metadataId, documentationTabForParentType(item.parentType));
        }
      }
    }
  }

  return summary;
}

const parentTypeToTab = ConfigurationTypeToDocumentationTabType as Record<
  string,
  DocumentationTabTypes | undefined
>;

function documentationTabForParentType(parentType: string): DocumentationTabTypes {
  return parentTypeToTab[parentType] ?? DocumentationTabTypes.OTHER;
}

export function configurationItemMapFromSalesforceItems(
  items: SalesforceItem[],
): ConfigurationItemMap {
  return groupBy(items, 'parentType') as ConfigurationItemMap;
}

export function configurationItemMapFromApiMetadataElements(
  elements: DocumentationUserInputMetadataElement[] | undefined,
): ConfigurationItemMap {
  if (!elements?.length) return {};
  return configurationItemMapFromSalesforceItems(
    elements.map(userInputMetadataElementToSalesforceItem),
  );
}

/** Builds `tagsAndUsersCountSummary[crmOrgId]` from tag/user API metadata (for grid selectors). */
export function buildTagsAndUsersCountSummaryFromMetadata(
  tagsList: DocumentationTagAndMetadataElementsDto[],
  usersWithMetadataList: DocumentationUserAndMetadataElementsDto[],
): Record<string, Record<string, string[]>> {
  const summary: Record<string, Record<string, string[]>> = {};

  const push = (rowId: string, metadataId: string, tab: DocumentationTabTypes) => {
    if (tab === DocumentationTabTypes.OTHER) return;
    if (!summary[rowId]) summary[rowId] = {};
    const bucket = summary[rowId][tab] ?? (summary[rowId][tab] = []);
    if (!bucket.includes(metadataId)) bucket.push(metadataId);
  };

  for (const tag of tagsList) {
    for (const el of tag.metadataElements ?? []) {
      const item = userInputMetadataElementToSalesforceItem(el);
      push(tag.id, userInputMetadataElementId(el), documentationTabForParentType(item.parentType));
    }
  }

  for (const u of usersWithMetadataList) {
    for (const el of [...u.ownerMetadataElements, ...u.collaboratorMetadataElements]) {
      const item = userInputMetadataElementToSalesforceItem(el);
      push(
        u.userId,
        userInputMetadataElementId(el),
        documentationTabForParentType(item.parentType),
      );
    }
  }

  return summary;
}
